"use client";
import clsx from "clsx";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { HiPlusCircle } from "react-icons/hi";

import { Image } from "builder/property/buildProperty";
import CrossIconForUploader from "components/CrossIconForUploader";
import UploadErrorModal from "components/UploadErrorModal";
import { VALIDATION_MESSAGE, VALIDATION_RULES } from "constants/validation";
import { cn } from "utils/cn";
import { AcceptFileType } from "utils/modalMessage.util";

const VALIDATION_CODE = {
  FILE_INVALID_TYPE: "file-invalid-type", // Dropzoneのデフォルトのエラーコード
  FILE_TOO_LARGE: "file-too-large", // カスタムのエラーコード
} as const;

const accept = {
  "image/jpeg": [".jpg"],
  "image/png": [".png"],
};

export type ImageUploaderProps = {
  images?: Image[];
  imagesWrapper?: string;
  onFilesChange: (files: File[]) => void;
  onRemoveCurrentImage?: (image: Image) => void;
  isError?: boolean;
  isSingle?: boolean;
  dataTestId?: string;
};

const ImageUploader = memo(({ images, imagesWrapper, onFilesChange, onRemoveCurrentImage, isError = false, isSingle = false, dataTestId }: ImageUploaderProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [currentImages, setCurrentImages] = useState<Image[]>();
  const onFilesChangeRef = useRef(onFilesChange);
  const borderClasses = isError ? "bg-[#fdf2e1] border-attention" : "border-input-normal border-input-normal";

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (isSingle) {
        // Single mode: replace all files with the first accepted file
        setFiles(acceptedFiles.slice(0, 1));
      } else {
        // Multiple mode: add to existing files
        setFiles((oldFiles) => [...oldFiles, ...acceptedFiles]);
      }
    },
    [isSingle]
  );

  // ファイルのバリデーション
  const fileValidator = useCallback((file: File) => {
    // ファイルサイズが20MB以下かどうか
    // 20MBはプロジェクト統一で制限
    const isFileSizeValid = VALIDATION_RULES.MAX_FILE_UPLOAD_SIZE >= file.size;
    if (!isFileSizeValid) {
      return {
        code: VALIDATION_CODE.FILE_TOO_LARGE,
        message: VALIDATION_MESSAGE.MAX_FILE_UPLOAD_SIZE,
      };
    }

    return null;
  }, []);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept,
    multiple: !isSingle,
    validator: fileValidator,
  });

  // FILE_INVALID_TYPEの場合のみエラーモーダルを表示するためその判定
  // DOCS: https://docs.google.com/spreadsheets/d/1rMG3osqYUXwT9x_n6MlF2M3tCE2BY0Zhnc_vH7yfSBg/edit#gid=444086591
  const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);
  const isErrorInvalidType = useCallback(
    () => fileRejections.some(({ errors: [{ code }] }) => code === VALIDATION_CODE.FILE_INVALID_TYPE),
    [fileRejections]
  );

  useMemo(() => {
    if (isErrorInvalidType()) {
      setIsOpenErrorModal(true);
    }
  }, [isErrorInvalidType]);

  const handleRemove = (fileToRemove: File) => {
    setFiles((oldFiles) => oldFiles.filter((file) => file !== fileToRemove));
  };

  // handleRemoveと同様の処理にすると無限ループが発生するので別途実装方法を変えた
  const handleRemoveCurrentImage = (imageToRemove: Image) => {
    setCurrentImages((prev) => prev?.filter((image) => image !== imageToRemove));
    onRemoveCurrentImage?.(imageToRemove);
  };

  useEffect(() => {
    onFilesChangeRef.current = onFilesChange;
  }, [onFilesChange]);

  useEffect(() => {
    if (files.length > 0) {
      onFilesChangeRef.current(files);
    }
  }, [files]);

  useEffect(() => {
    // Set currentImages to images prop (including empty array)
    setCurrentImages(images || []);
  }, [images]);

  return (
    <div className="relative flex flex-col justify-center">
      <div className={cn("flex flex-wrap gap-4 w-fit", imagesWrapper)} data-testid={dataTestId}>
        {currentImages?.map((image, index) => (
          <div
            key={index}
            className={cn("relative h-[120px] w-[120px]  border border-gray", { "last:mr-4": !isSingle })}
          >
            <CrossIconForUploader onClick={() => handleRemoveCurrentImage(image)} />

            <img src={image.storagePath} alt={`preview ${index}`} className="h-full w-full object-cover" />
          </div>
        ))}
        {files.map((file, index) => (
          <div
            key={index}
            className={cn("relative h-[120px] w-[120px]  border border-gray", { "last:mr-4": !isSingle })}
          >
            <CrossIconForUploader onClick={() => handleRemove(file)} />
            <img src={URL.createObjectURL(file)} alt={`preview ${index}`} className="h-full w-full object-cover" />
          </div>
        ))}
        {(!isSingle || (isSingle && files.length === 0 && (!currentImages || currentImages.length === 0))) && (
          <div
            {...getRootProps()}
            className={clsx(
              "flex h-[120px] w-[120px] cursor-pointer items-center justify-center border-2  border-dashed ",
              borderClasses
            )}
          >
            <input {...getInputProps()} id="file" name="file" type="file" aria-label="file-select" />
            <HiPlusCircle size={30} fill="#706D65" />
          </div>
        )}
      </div>

      {
        // FILE_TOO_LARGEの場合のみバリデーションメッセージを表示
        // DOCS: https://docs.google.com/spreadsheets/d/1rMG3osqYUXwT9x_n6MlF2M3tCE2BY0Zhnc_vH7yfSBg/edit#gid=444086591
        fileRejections
          .map(({ errors: [{ message, code }] }) => ({
            message,
            code,
          }))
          .map(
            ({ code }, index) =>
              code !== VALIDATION_CODE.FILE_INVALID_TYPE && (
                <p key={index} className="mt-1 min-w-max text-sm text-attention">
                  {code === VALIDATION_CODE.FILE_TOO_LARGE
                    ? VALIDATION_MESSAGE.MAX_FILE_UPLOAD_SIZE
                    : "予期せぬエラーが発生しました"}
                </p>
              )
          )
      }
      <UploadErrorModal
        isOpen={isOpenErrorModal}
        onClose={() => {
          setIsOpenErrorModal(false);
        }}
        acceptFileTypes={Object.keys(accept) as AcceptFileType[]}
      />
    </div>
  );
});

ImageUploader.displayName = "ImageUploader";

export default ImageUploader;
